/**
 * Frontend mirror of backend/deal_config.
 *
 * PR Spine-1b1 (2026-05-29) rewrite.
 * ==================================
 * Previously this file mirrored the flat 10-status enum. Backend PR Spine-1a
 * replaced that with a 6+2 stage spine, each stage containing 1-5 substages
 * (25 forward + 2 non-linear). The application's `status` field now holds
 * a substage id; the parent stage is stored alongside as `stage`.
 *
 * What this file exports:
 *   - DEAL_STAGES            — 6 forward stages + on_hold + cancelled
 *   - DEAL_SUBSTAGES         — 27 substages with `stage`, `label`, `order`
 *   - SUBSTAGES_BY_STAGE     — { stageId: [substage, ...] } — for grouped <Select>
 *   - CANCELLED_REASONS      — enum displayed in the Cancel modal
 *   - STATUS_STYLE           — { substageId: {color, icon, label} } — used by
 *                              statusConfig in AdminDashboard for the dropdown
 *                              chip styling. Colour comes from the parent stage.
 *   - CREATABLE_SUBSTAGES    — subset shown in "Create Application" modal
 *   - getStageLabel(id)      — convenience formatter for a STAGE id
 *   - getSubstageLabel(id)   — convenience formatter for a SUBSTAGE id
 *   - getStageForSubstage(id)— parent stage id for a substage id
 *   - getSubstageStyle(id)   — full style object for a substage chip
 *
 * Backward compatibility:
 * The old DEAL_STAGES list (11 statuses) is GONE. Call sites that did
 * `DEAL_STAGES.map((s) => <SelectItem ...>)` get a different shape now —
 * the new DEAL_STAGES is the 8 STAGE entries, not substages. Most of those
 * call sites were status filters that should now filter by stage anyway.
 * For places that need the full substage list (e.g. the per-application
 * status picker), use the new <StatusSelector> component.
 */

// Tailwind colour classes per stage — must match the hex colours in
// backend/deal_config.DEAL_STAGES so dropdowns, chips, and Kanban columns
// share one visual language.
const STAGE_PALETTE = {
  lead:             { color: "bg-gray-100 text-gray-700 border-gray-200",       hex: "#6b7280" },
  assessment:       { color: "bg-purple-100 text-purple-700 border-purple-200", hex: "#8b5cf6" },
  lender_submitted: { color: "bg-amber-100 text-amber-700 border-amber-200",    hex: "#f59e0b" },
  formal_approval:  { color: "bg-cyan-100 text-cyan-700 border-cyan-200",       hex: "#06b6d4" },
  settlement:       { color: "bg-green-100 text-green-700 border-green-200",    hex: "#22c55e" },
  post_settlement:  { color: "bg-emerald-100 text-emerald-700 border-emerald-200", hex: "#10b981" },
  non_finance:      { color: "bg-indigo-100 text-indigo-700 border-indigo-200", hex: "#6366f1" },
  on_hold:          { color: "bg-slate-100 text-slate-700 border-slate-200",    hex: "#64748b" },
  cancelled:        { color: "bg-red-100 text-red-700 border-red-200",          hex: "#ef4444" },
};

// 8 top-level stages — Kanban columns, status filters, parent of substages.
export const DEAL_STAGES = [
  { id: "lead",             label: "Lead",             order: 1,  nonLinear: false, terminal: false, ...STAGE_PALETTE.lead },
  { id: "assessment",       label: "Assessment",       order: 2,  nonLinear: false, terminal: false, ...STAGE_PALETTE.assessment },
  { id: "lender_submitted", label: "Lender Submitted", order: 3,  nonLinear: false, terminal: false, ...STAGE_PALETTE.lender_submitted },
  { id: "formal_approval",  label: "Formal Approval",  order: 4,  nonLinear: false, terminal: false, ...STAGE_PALETTE.formal_approval },
  { id: "settlement",       label: "Settlement",       order: 5,  nonLinear: false, terminal: false, ...STAGE_PALETTE.settlement },
  { id: "post_settlement",  label: "Post Settlement",  order: 6,  nonLinear: false, terminal: false, ...STAGE_PALETTE.post_settlement },
  { id: "non_finance",      label: "Non-Finance",      order: 96, nonLinear: true,  terminal: false, ...STAGE_PALETTE.non_finance },
  { id: "on_hold",          label: "On Hold",          order: 97, nonLinear: true,  terminal: false, ...STAGE_PALETTE.on_hold },
  { id: "cancelled",        label: "Cancelled",        order: 98, nonLinear: true,  terminal: true,  ...STAGE_PALETTE.cancelled },
];

// 27 substages — the values that actually live in `application.status`.
export const DEAL_SUBSTAGES = [
  // Lead
  { id: "lead_new",                   stage: "lead",             label: "Lead",                            order: 1 },
  { id: "lead_mandate_issued",        stage: "lead",             label: "Mandate Issued",                  order: 2 },
  { id: "lead_mandate_signed",        stage: "lead",             label: "Mandate Signed",                  order: 3 },
  { id: "lead_awaiting_docs",         stage: "lead",             label: "Awaiting Checklist Docs",         order: 4 },
  { id: "lead_application_completed", stage: "lead",             label: "Application Completed",           order: 5 },
  // Assessment
  { id: "assess_review_strategy",     stage: "assessment",       label: "Review & Strategy",               order: 1 },
  { id: "assess_lender_match",        stage: "assessment",       label: "Lender Match",                    order: 2 },
  { id: "assess_im_preparation",      stage: "assessment",       label: "IM Preparation",                  order: 3 },
  // Lender Submitted
  { id: "lender_submitted",           stage: "lender_submitted", label: "Submitted to Lender",             order: 1 },
  { id: "lender_indicative_received", stage: "lender_submitted", label: "Indicative Terms Received",       order: 2 },
  { id: "lender_indicative_accepted", stage: "lender_submitted", label: "Indicative Accepted",             order: 3 },
  { id: "lender_valuation",           stage: "lender_submitted", label: "Valuation In Progress",           order: 4 },
  { id: "lender_preconditions",       stage: "lender_submitted", label: "Preconditions In Progress",       order: 5 },
  // Formal Approval
  { id: "approval_offer_received",    stage: "formal_approval",  label: "Formal Offer Received",           order: 1 },
  { id: "approval_loan_docs_issued",  stage: "formal_approval",  label: "Loan Documents Issued",           order: 2 },
  { id: "approval_final_conditions",  stage: "formal_approval",  label: "Final Conditions",                order: 3 },
  { id: "approval_sign_off",          stage: "formal_approval",  label: "Documents Sign-Off",              order: 4 },
  // Settlement
  { id: "settlement_booked",          stage: "settlement",       label: "Settlement Booked",               order: 1 },
  { id: "settlement_in_progress",     stage: "settlement",       label: "Settlement In Progress",          order: 2 },
  { id: "settlement_settled",         stage: "settlement",       label: "Settled",                         order: 3 },
  // Post Settlement
  { id: "post_active",                stage: "post_settlement",  label: "Active Loan",                     order: 1 },
  { id: "post_3mo_review",            stage: "post_settlement",  label: "3-Month Follow-up",               order: 2 },
  { id: "post_6mo_review",            stage: "post_settlement",  label: "6-Month Follow-up",               order: 3 },
  { id: "post_12mo_review",           stage: "post_settlement",  label: "10-12 Month Borrower Intentions", order: 4 },
  { id: "post_paid_out",              stage: "post_settlement",  label: "Loan Paid Out",                   order: 5 },
  // Non-linear
  { id: "nonfin_sale_of_asset",       stage: "non_finance",      label: "Sale of Asset",                   order: 1 },
  { id: "nonfin_joint_venture",       stage: "non_finance",      label: "Joint Venture",                   order: 2 },
  { id: "nonfin_other",               stage: "non_finance",      label: "Other",                           order: 3 },
  { id: "on_hold",                    stage: "on_hold",          label: "On Hold",                         order: 1 },
  { id: "cancelled",                  stage: "cancelled",        label: "Cancelled",                       order: 1 },
];

// Group substages under their stage for use in a grouped <Select>.
export const SUBSTAGES_BY_STAGE = DEAL_STAGES.reduce((acc, stage) => {
  acc[stage.id] = DEAL_SUBSTAGES
    .filter((s) => s.stage === stage.id)
    .sort((a, b) => a.order - b.order);
  return acc;
}, {});

// Cancelled-reason enum — Cancelled modal shows these as radio options.
export const CANCELLED_REASONS = [
  { id: "lost_to_competitor",   label: "Lost to Competitor" },
  { id: "client_withdrew",      label: "Client Withdrew" },
  { id: "lender_declined",      label: "Lender Declined" },
  { id: "no_longer_proceeding", label: "No Longer Proceeding" },
  { id: "other",                label: "Other" },
];

// Visual style per substage — derived from the parent stage palette. Used
// in AdminDashboard's statusConfig and any chip that displays a substage.
// The icon is set by the consumer (we can't import lucide here without
// creating a circular dep with the styled chip component).
export const STATUS_STYLE = DEAL_SUBSTAGES.reduce((acc, sub) => {
  const stagePalette = STAGE_PALETTE[sub.stage];
  acc[sub.id] = {
    color: stagePalette?.color || "bg-gray-100 text-gray-600 border-gray-200",
    hex: stagePalette?.hex || "#6b7280",
    label: sub.label,
    stage: sub.stage,
    stageLabel: DEAL_STAGES.find((s) => s.id === sub.stage)?.label || sub.stage,
  };
  return acc;
}, {});

// Subset shown when admin creates an application from scratch — they
// should only ever seed at the start of the workflow. Later substages
// arrive via the natural status-change flow with gating enforced.
export const CREATABLE_SUBSTAGES = [
  { id: "lead_new",                   stage: "lead",       label: "Lead" },
  { id: "lead_mandate_issued",        stage: "lead",       label: "Mandate Issued" },
  { id: "lead_mandate_signed",        stage: "lead",       label: "Mandate Signed" },
  { id: "lead_awaiting_docs",         stage: "lead",       label: "Awaiting Checklist Docs" },
  { id: "lead_application_completed", stage: "lead",       label: "Application Completed" },
  { id: "assess_review_strategy",     stage: "assessment", label: "Review & Strategy (Assessment)" },
];

// Backward-compat alias for any code that imported CREATABLE_STATUSES.
// Existing call sites get a sensible default — the early substages —
// without needing to be updated in this PR.
export const CREATABLE_STATUSES = CREATABLE_SUBSTAGES;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function getStageLabel(id) {
  return DEAL_STAGES.find((s) => s.id === id)?.label || id;
}

export function getSubstageLabel(id) {
  return DEAL_SUBSTAGES.find((s) => s.id === id)?.label || id;
}

export function getStageForSubstage(substageId) {
  return DEAL_SUBSTAGES.find((s) => s.id === substageId)?.stage || null;
}

export function getSubstageStyle(substageId) {
  return (
    STATUS_STYLE[substageId] || {
      color: "bg-gray-100 text-gray-600 border-gray-200",
      hex: "#6b7280",
      label: (substageId || "").replace(/_/g, " "),
      stage: "lead",
      stageLabel: "Lead",
    }
  );
}

// Convenience: list all substages in pipeline order — useful for the
// pipeline timeline view (Spine-1b2). Skips non-linear.
export function forwardSubstagesInOrder() {
  return DEAL_STAGES
    .filter((s) => !s.nonLinear)
    .flatMap((stage) => SUBSTAGES_BY_STAGE[stage.id] || []);
}

// ---------------------------------------------------------------------------
// PR Spine-1c — Per-substage checklist templates
// ---------------------------------------------------------------------------
// Mirror of backend/deal_config.STAGE_CHECKLIST_TEMPLATES. Each item has:
//   id          — stable kebab-case slug, unique within the substage
//   label       — human-readable
//   source_hint — which integration will auto-tick this in Spine-1d:
//                 "manual" | "xero" | "envelope" | "drive" | "calendar"
//
// Used by <ChecklistPanel> to render items even before any progress has been
// stored. The backend overlays stored progress on top of these templates when
// the panel calls GET /admin/applications/{id}/checklist.
export const STAGE_CHECKLIST_TEMPLATES = {
  // Stage 1 — Lead
  lead_new: [
    { id: "discovery_call_done",  label: "Discovery call done",                source_hint: "manual" },
  ],
  lead_mandate_issued: [
    { id: "mandate_sent",           label: "Mandate sent to client",           source_hint: "envelope" },
    { id: "mandate_invoice_issued", label: "Mandate invoice issued",           source_hint: "xero" },
    { id: "reminder_sent",          label: "Reminder sent (if 7+ days)",       source_hint: "manual" },
  ],
  lead_mandate_signed: [
    { id: "mandate_signed_client",  label: "Mandate signed by client",          source_hint: "envelope" },
    { id: "mandate_countersigned",  label: "Mandate countersigned by Solara",   source_hint: "envelope" },
    { id: "mandate_invoice_paid",   label: "Mandate invoice paid",              source_hint: "xero" },
    { id: "engagement_email_sent",  label: "Engagement confirmation email sent",source_hint: "manual" },
  ],
  lead_awaiting_docs: [],  // dynamically populated from loan-type checklist
  lead_application_completed: [
    { id: "app_reviewed",           label: "Application reviewed for completeness", source_hint: "manual" },
    { id: "ready_for_assessment",   label: "Ready for Assessment handoff",     source_hint: "manual" },
  ],
  // Stage 2 — Assessment
  assess_review_strategy: [
    { id: "strategy_internal",      label: "Strategy approved internally",     source_hint: "manual" },
    { id: "pattern_selected",       label: "Pattern from Solution Library selected", source_hint: "manual" },
  ],
  assess_lender_match: [
    { id: "shortlist_drafted",      label: "Lender shortlist drafted",         source_hint: "manual" },
    { id: "shortlist_signed_off",   label: "Shortlist signed off",             source_hint: "manual" },
  ],
  assess_im_preparation: [
    { id: "im_draft_v1",            label: "IM draft v1",                      source_hint: "manual" },
    { id: "im_final_approved",      label: "IM final approved",                source_hint: "manual" },
    { id: "im_uploaded",            label: "IM uploaded to deal Drive folder", source_hint: "drive" },
  ],
  // Stage 3 — Lender Submitted
  lender_submitted: [
    { id: "submission_email_sent",     label: "Submission email sent",         source_hint: "manual" },
    { id: "lender_receipt_confirmed",  label: "Confirmation of receipt from lender", source_hint: "manual" },
  ],
  lender_indicative_received: [
    { id: "indicative_reviewed",    label: "Indicative reviewed with client",   source_hint: "manual" },
    { id: "comparison_prepared",    label: "Comparison sheet (if multiple lenders)", source_hint: "manual" },
  ],
  lender_indicative_accepted: [
    { id: "client_acceptance",      label: "Client acceptance recorded in writing", source_hint: "manual" },
  ],
  lender_valuation: [
    { id: "valuation_ordered",      label: "Valuation ordered",                 source_hint: "manual" },
    { id: "valuation_instructed",   label: "Val instructed to valuer",          source_hint: "manual" },
    { id: "valuation_received",     label: "Valuation received",                source_hint: "drive" },
  ],
  lender_preconditions: [],  // dynamic per lender
  // Stage 4 — Formal Approval
  approval_offer_received: [
    { id: "offer_reviewed",         label: "Offer reviewed with client",        source_hint: "manual" },
    { id: "offer_accepted",         label: "Offer accepted by client",          source_hint: "manual" },
    { id: "lender_fees_invoiced",   label: "Lender fees invoiced",              source_hint: "xero" },
    { id: "lender_fees_paid",       label: "Lender fees paid",                  source_hint: "xero" },
  ],
  approval_loan_docs_issued: [
    { id: "loan_docs_received",     label: "Loan docs received from lender",    source_hint: "manual" },
    { id: "loan_docs_reviewed",     label: "Loan docs reviewed",                source_hint: "manual" },
    { id: "loan_docs_sent",         label: "Loan docs sent to client",          source_hint: "envelope" },
    { id: "loan_docs_signed",       label: "Loan docs signed by client",        source_hint: "envelope" },
  ],
  approval_final_conditions: [],  // dynamic per lender
  approval_sign_off: [
    { id: "commission_invoice",     label: "Solara commission invoice raised to lender", source_hint: "xero" },
    { id: "lender_sign_off",        label: "Lender internal sign-off received", source_hint: "manual" },
    { id: "all_conditions_cleared", label: "All conditions cleared",            source_hint: "manual" },
  ],
  // Stage 5 — Settlement
  settlement_booked: [
    { id: "settlement_date",        label: "Settlement date confirmed",         source_hint: "calendar" },
    { id: "solicitor_confirmed",    label: "Settlement agent / solicitor confirmed", source_hint: "manual" },
    { id: "insurance_confirmed",    label: "Insurance confirmed",               source_hint: "manual" },
  ],
  settlement_in_progress: [
    { id: "settlement_attended",    label: "Settlement attended",               source_hint: "manual" },
    { id: "funds_transfer",         label: "Funds transfer confirmed",          source_hint: "manual" },
  ],
  settlement_settled: [
    { id: "title_registered",       label: "Title registered",                  source_hint: "manual" },
    { id: "lender_commission",      label: "Lender commission received",        source_hint: "xero" },
    { id: "settlement_notify",      label: "Settlement notification to client", source_hint: "manual" },
  ],
  // Stage 6 — Post Settlement
  post_active: [
    { id: "congrats_letter",        label: "Congratulations letter sent",       source_hint: "manual" },
    { id: "post_settlement_pack",   label: "Post-settlement pack sent",         source_hint: "manual" },
  ],
  post_3mo_review: [
    { id: "3mo_scheduled",          label: "3-month check-in scheduled",        source_hint: "calendar" },
    { id: "3mo_done",               label: "3-month check-in done",             source_hint: "manual" },
  ],
  post_6mo_review: [
    { id: "6mo_scheduled",          label: "6-month check-in scheduled",        source_hint: "calendar" },
    { id: "6mo_done",               label: "6-month check-in done",             source_hint: "manual" },
  ],
  post_12mo_review: [
    { id: "refinance_initiated",    label: "Refinance conversation initiated",  source_hint: "manual" },
    { id: "intentions_captured",    label: "Borrower intentions captured",      source_hint: "manual" },
    { id: "refinance_tracked",      label: "Refinance opportunity tracked in CRM", source_hint: "manual" },
  ],
  post_paid_out: [
    { id: "final_commission",       label: "Final commission reconciled",       source_hint: "xero" },
    { id: "loan_closed",            label: "Loan marked closed",                source_hint: "manual" },
  ],
  // Non-linear
  nonfin_sale_of_asset: [],
  nonfin_joint_venture: [],
  nonfin_other:         [],
  on_hold:   [],
  cancelled: [],
};

// Helper: return the next substage id in the forward progression, or null
// if at terminal/non-linear/end. Mirrors backend get_next_substage_id().
export function getNextSubstageId(currentSubstageId) {
  const sub = DEAL_SUBSTAGES.find((s) => s.id === currentSubstageId);
  if (!sub || DEAL_STAGES.find((s) => s.id === sub.stage)?.nonLinear) return null;
  const forward = DEAL_STAGES
    .filter((s) => !s.nonLinear)
    .flatMap((stage) => SUBSTAGES_BY_STAGE[stage.id] || []);
  const idx = forward.findIndex((s) => s.id === currentSubstageId);
  if (idx === -1) return null;
  return idx + 1 < forward.length ? forward[idx + 1].id : null;
}
